import { type FC, useMemo } from 'react'
import { createRoot } from 'react-dom/client'
import { IntlProvider } from 'react-intl'
import createCache from '@emotion/cache'
import { CacheProvider, Global } from '@emotion/react'
import { CssBaseline } from '@mui/material'
import { createTheme, ThemeProvider } from '@mui/material/styles'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { format } from 'date-fns'
import { WidgetSdkFactory } from 'src/widgets/services/WidgetSdkFactory'

import {
  ARROW_DOWN,
  ARROW_UP,
  ENTER,
  ESCAPE,
  KEYDOWN_EVENT,
  KEYPRESS_EVENT,
  KEYUP_EVENT,
  TAB,
} from '@Constants/keyboard'
import { DEFAULT_LOCALE } from '@Constants/locale'
import { SdkProvider } from '@Contexts/sdk/SdkContext'
import { type AutocompleteResultModel } from '@DS/components/navigation/search/atoms/AutocompleteResult'
import { useAllMessages } from '@Hooks/i18n'
import { lightTheme } from '@Material/theme'
import { NEXT_ROUTES } from '@Routes/routes'

import { externalWidgetPublicRuntimeConfig } from '../config'

import { POPPINS_BOLD_WOFF } from './fonts/poppinsBold'
import { POPPINS_MEDIUM_WOFF } from './fonts/poppinsMedium'
import { POPPINS_SEMI_BOLD_WOFF } from './fonts/poppinsSemiBold'
import { type ODSearchExternalAppProps, type ODSearchExternalWidgetInit } from './ODSearchExternal.types'
import ODSearchExternalWidget from './ODSearchExternalWidget'
import * as styles from './styles'
import { WIDGET_CLASS_NAME } from './styles'

const KEYBOARD_EVENT_TYPES = [KEYDOWN_EVENT, KEYUP_EVENT, KEYPRESS_EVENT]
const PASS_THROUGH_KEYS = new Set([ARROW_UP, ARROW_DOWN, TAB, ENTER, ESCAPE])

export const ODSearchExternalApp: FC<ODSearchExternalAppProps> = ({
  locale = DEFAULT_LOCALE,
  isRedirectInNewTab = false,
}) => {
  const { messages } = useAllMessages(locale)

  const { frontendPublicUrl, basePath, regionSudURL } = externalWidgetPublicRuntimeConfig

  const autocompleteSdk = useMemo(
    () => WidgetSdkFactory.getAutocompleteSDK(locale, frontendPublicUrl),
    [locale, frontendPublicUrl],
  )

  const queryClient = new QueryClient()

  if (!messages || !frontendPublicUrl || !basePath || !regionSudURL) {
    return null
  }

  const onSubmit = (origin: AutocompleteResultModel, destination: AutocompleteResultModel, outwardDate?: Date) => {
    if (!outwardDate) {
      return
    }

    const formattedOutwardDate = format(outwardDate, 'yyyy-MM-dd-HH-mm')
    window.open(
      `${frontendPublicUrl}${basePath}/${locale}${NEXT_ROUTES.redirect}?redirection_type=SEARCH&origin_id=${origin.id}&destination_id=${destination.id}&outward_date=${formattedOutwardDate}`,
      isRedirectInNewTab ? '_blank' : '_self',
    )
  }

  return (
    <QueryClientProvider client={queryClient}>
      <IntlProvider locale={locale as string} defaultLocale={locale} messages={messages}>
        <SdkProvider customSdk={{ autocompleteSdk }}>
          <ODSearchExternalWidget
            homePath={frontendPublicUrl}
            regionSudURL={regionSudURL}
            isExternalSubmitButton={isRedirectInNewTab}
            onSubmit={onSubmit}
          />
        </SdkProvider>
      </IntlProvider>
    </QueryClientProvider>
  )
}

export const init: ODSearchExternalWidgetInit = (containerId, parameters: ODSearchExternalAppProps) => {
  const container = document.getElementById(containerId) as HTMLElement
  const shadowContainer = container.shadowRoot ?? container.attachShadow({ mode: 'open' })

  // We exit if the widget is already present
  if (shadowContainer.querySelector(`.${WIDGET_CLASS_NAME}`)) {
    return
  }

  KEYBOARD_EVENT_TYPES.forEach((type) => {
    shadowContainer.addEventListener(
      type,
      (e) => {
        if (!(e instanceof KeyboardEvent)) {
          return
        }

        if (PASS_THROUGH_KEYS.has(e.key)) {
          return
        }

        if (e.ctrlKey || e.metaKey || e.altKey) {
          return
        }

        e.stopPropagation()
      },
      { capture: true },
    )
  })

  const shadowRootElement = document.createElement('div')
  shadowRootElement.className = WIDGET_CLASS_NAME

  // MUI's FocusTrap is broken inside shadow DOM: focus events reaching document have their
  // event.target retargeted to the shadow host, so rootElement.contains(event.target) is always
  // false. MUI therefore thinks focus has escaped, causing an infinite redirect loop.
  // disableEnforceFocus: true (set on MuiDialog/MuiModal below) disables the broken listener.
  // We implement our own Tab-key trap here, scoped to the visible [role="dialog"].
  shadowContainer.addEventListener(
    'keydown',
    (e: Event) => {
      if (!(e instanceof KeyboardEvent) || e.key !== TAB) {
        return
      }

      const dialog = shadowRootElement.querySelector<HTMLElement>('[role="dialog"]')

      if (!dialog || dialog.offsetParent === null) {
        return
      }

      const currentFocus = shadowContainer.activeElement

      if (!currentFocus || !dialog.contains(currentFocus)) {
        return
      }

      const focusableSelector = [
        'button:not([disabled])',
        'input:not([disabled])',
        'select:not([disabled])',
        'textarea:not([disabled])',
        'a[href]',
        '[tabindex]:not([tabindex="-1"])',
      ].join(',')

      const focusable = [...dialog.querySelectorAll<HTMLElement>(focusableSelector)].filter(
        (element) =>
          !(
            !element.dataset.testid?.startsWith('sentinel') &&
            (element.offsetParent !== null || element.getBoundingClientRect().width > 0)
          ),
      )

      if (!focusable.length) {
        return
      }

      const first = focusable[0]
      const last = focusable[focusable.length - 1]

      if (e.shiftKey && currentFocus === first) {
        e.preventDefault()
        last.focus()
      } else if (!e.shiftKey && currentFocus === last) {
        e.preventDefault()
        first.focus()
      }
    },
    { capture: true },
  )

  // In shadow DOM, document.activeElement always returns the shadow HOST element (the container
  // div), never the actual focused element inside the shadow root. MUI's MenuList keyboard
  // navigation relies on document.activeElement to determine the currently focused item
  // (see MenuList.js: ownerDocument(list).activeElement). We patch it to return
  // shadowRoot.activeElement when focus is inside this widget.
  // NOTE: this patch is instance-scoped; if multiple widgets are mounted, the last one wins.
  const nativeActiveElementDescriptor = Object.getOwnPropertyDescriptor(Document.prototype, 'activeElement')

  if (nativeActiveElementDescriptor?.get) {
    const getNativeActiveElement = nativeActiveElementDescriptor.get

    Object.defineProperty(document, 'activeElement', {
      get() {
        const nativeActive = getNativeActiveElement.call(document)

        if (nativeActive === container) {
          return shadowContainer.activeElement ?? nativeActive
        }

        return nativeActive
      },
      configurable: true,
    })
  }

  // The font-face configurations have to be put in the head of the document
  const headStyleElement = document.createElement('style')
  headStyleElement.innerHTML = `
    @font-face {
      font-family: Poppins;
      src: url("data:application/font-woff2;base64,${POPPINS_MEDIUM_WOFF}") format('woff'), url("data:application/font-woff2;base64,${POPPINS_MEDIUM_WOFF}") format('woff');
      font-weight: 500;
      font-style: normal;
      font-display: swap;
    }
    @font-face {
      font-family: Poppins-Bold;
      src: url("data:application/font-woff2;base64,${POPPINS_BOLD_WOFF}") format('woff'), url("data:application/font-woff2;base64,${POPPINS_BOLD_WOFF}") format('woff');
      font-weight: 700;
      font-style: normal;
      font-display: swap;
    }
    @font-face {
      font-family: Poppins-SemiBold;
      src: url("data:application/font-woff2;base64,${POPPINS_SEMI_BOLD_WOFF}") format('woff'), url("data:application/font-woff2;base64,${POPPINS_SEMI_BOLD_WOFF}") format('woff');
      font-weight: 700;
      font-style: normal;
      font-display: swap;
    }
  `
  document.head.appendChild(headStyleElement)

  const styleElement = document.createElement('style')

  const widgetTheme = createTheme(lightTheme, {
    components: {
      MuiPopover: {
        defaultProps: {
          container: shadowRootElement,
          // MUI's FocusTrap uses event.target to detect focus-escape. In shadow DOM,
          // event.target is retargeted to the shadow host, so rootElement.contains() always
          // returns false → MUI incorrectly thinks focus escaped → broken keyboard behavior.
          disableEnforceFocus: true,
        },
      },
      MuiDialog: {
        defaultProps: {
          container: shadowRootElement,
          disableEnforceFocus: true,
        },
      },
      MuiModal: {
        defaultProps: {
          container: shadowRootElement,
          disableEnforceFocus: true,
        },
      },
    },
  })

  shadowContainer.appendChild(styleElement)
  shadowContainer.appendChild(shadowRootElement)

  const cache = createCache({
    key: 'css',
    container: styleElement,
    insertionPoint: shadowRootElement,
  })

  createRoot(shadowRootElement).render(
    <CacheProvider value={cache}>
      <ThemeProvider theme={widgetTheme}>
        <CssBaseline />
        <Global styles={styles.global} />
        <ODSearchExternalApp {...parameters} />
      </ThemeProvider>
    </CacheProvider>,
  )
}
